/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { SectionCard } from '@features/property-management/components/PropertyDetailView/SectionCard';
import { getClientAvatarAccentColor } from '../../utils/avatar-accent';
import { useAgencyMemberName, useClientResponsible } from '../../hooks/useClientResponsible';

export interface ResponsibleCardProps {
  agencyId: string;
  clientId: string;
  /** `assignment.primary_broker` off the client record. */
  primaryBroker?: string;
  /** `assignment.captured_by` off the client record. */
  capturedBy?: string;
  /** `assignment.external_captor`: a captor who is not in the house, by name. */
  externalCaptor?: string;
  /** `assignment.external_broker`: a broker outside the agency who works the client, by name. */
  externalBroker?: string;
  onViewAttendance: (dealId: string) => void;
}

/**
 * The broker's initials, at most two.
 *
 * @param name - The broker's display name.
 * @returns One or two uppercase initials.
 */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

interface BrokerRowProps {
  name: string;
  role: string;
  testId: string;
}

/**
 * One named broker: avatar, name, and what they are to this client.
 *
 * @param props - The display name, the role line, and a test id.
 * @returns The row.
 */
function BrokerRow({ name, role, testId }: BrokerRowProps) {
  return (
    <div data-testid={testId} className="flex items-center gap-(--space-2-5)">
      <Text
        component="span"
        className="flex h-[32px] w-[32px] flex-none items-center justify-center rounded-(--radius-full) text-[length:var(--text-meta)] [font-weight:var(--font-bold)] text-(--text-inverse)"
        style={{ background: getClientAvatarAccentColor(name) }}
      >
        {initialsOf(name)}
      </Text>
      <div className="flex min-w-0 flex-col">
        <Text
          component="span"
          className="[overflow-wrap:anywhere] text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] text-(--text-primary)"
        >
          {name}
        </Text>
        <Text component="span" className="text-[length:var(--text-meta)] text-(--text-tertiary-soft)">
          {role}
        </Text>
      </div>
    </div>
  );
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Whether two member e-mails name the same person.
 *
 * @param a - One e-mail.
 * @param b - The other.
 * @returns True when both are set and equal ignoring case.
 */
function sameMember(a?: string, b?: string): boolean {
  return Boolean(a && b && a.toLowerCase() === b.toLowerCase());
}

/**
 * The line under the responsible broker's name.
 *
 * @param attending - Whether they are also running the open attendance.
 * @param captured - Whether they also brought the client in.
 * @param t - The clients-namespace translation function.
 * @returns The role line.
 */
function ownerRole(attending: boolean, captured: boolean, t: Translate): string {
  if (attending) return t('detail.responsible.ownerAndAttending');
  return captured ? t('detail.responsible.ownerAndCaptor') : t('detail.responsible.owner');
}

/**
 * The brokers beside this client: the one who answers for the record and
 * the one handling them right now.
 *
 * The client record names its own responsible broker, the same way a
 * listing does, and an open attendance names whoever is working them at
 * the moment. Those are two different answers often given by two
 * different people, so the card shows both rather than letting one stand
 * in for the other — and collapses to a single row when they coincide.
 * Who captured the client (`captured_by`) gets a row of their own unless
 * they are the responsible broker, whose role line then says so. A captor
 * from outside the house (`external_captor`) has no account to look up, so
 * the name on file is shown as written. The card disappears only when nobody
 * is named.
 *
 * @param props - The agency, the client, its responsible broker, and the
 *   attendance callback.
 * @returns The card, or null when nobody is named.
 */
export function ResponsibleCard({
  agencyId,
  clientId,
  primaryBroker,
  capturedBy,
  externalCaptor,
  externalBroker,
  onViewAttendance,
}: ResponsibleCardProps) {
  const { t } = useTranslation('features/clients');
  const { ownerName, attendingName, dealId, otherOpenCount, samePerson } =
    useClientResponsible(agencyId, clientId, primaryBroker);
  const capturedName = useAgencyMemberName(capturedBy);
  const capturedIsOwner = sameMember(capturedBy, primaryBroker);
  const showsCaptor = Boolean(capturedName && !capturedIsOwner);

  if (!ownerName && !attendingName && !capturedName && !externalCaptor && !externalBroker) return null;

  const rowCount = [
    ownerName,
    attendingName && !samePerson,
    showsCaptor,
    externalCaptor,
    externalBroker,
  ].filter(Boolean).length;
  const showsBoth = rowCount > 1;
  const attendingRole = otherOpenCount > 0
    ? t('detail.responsible.alsoOpen', { count: otherOpenCount })
    : t('detail.responsible.viaAttendance');

  return (
    <SectionCard
      data-testid="client-responsible-card"
      title={showsBoth ? t('detail.responsible.titlePlural') : t('detail.responsible.title')}
    >
      <div className="mb-(--space-3) flex flex-col gap-(--space-3)">
        {ownerName && (
          <BrokerRow
            testId="client-responsible-owner"
            name={ownerName}
            role={ownerRole(samePerson, capturedIsOwner, t)}
          />
        )}
        {attendingName && !samePerson && (
          <BrokerRow
            testId="client-responsible-attending"
            name={attendingName}
            role={attendingRole}
          />
        )}
        {showsCaptor && capturedName && (
          <BrokerRow
            testId="client-responsible-captor"
            name={capturedName}
            role={t('detail.responsible.captor')}
          />
        )}
        {externalBroker && (
          <BrokerRow
            testId="client-responsible-external-broker"
            name={externalBroker}
            role={t('detail.responsible.externalBroker')}
          />
        )}
        {externalCaptor && (
          <BrokerRow
            testId="client-responsible-external-captor"
            name={externalCaptor}
            role={t('detail.responsible.externalCaptor')}
          />
        )}
      </div>
      {dealId && (
        <UnstyledButton
          type="button"
          data-testid="client-responsible-open"
          onClick={() => onViewAttendance(dealId)}
          className="h-[34px] w-full whitespace-nowrap rounded-(--radius-md) border border-solid border-(--border-medium) bg-(--surface-primary) px-(--space-3) text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-(--text-primary) cursor-pointer hover:bg-(--surface-hover)"
        >
          {t('detail.responsible.openAttendance')}
        </UnstyledButton>
      )}
    </SectionCard>
  );
}
